import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import ConfettiEffect from '../components/ConfettiEffect';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { 
  playExcavationChiselSound, 
  playCorrectChime, 
  playWinFanfare, 
  playLaunchChime 
} from '../utils/soundEffects';
import { useGameExitRoute } from '../utils/navigation';

const { FiAward, FiSun, FiMoon, FiHome, FiMaximize2, FiMinimize2, FiRefreshCw, FiZap, FiCheckCircle } = FiIcons;

// Prehistoric Fossil Specimens buried in the dig site
const FOSSIL_SPECIMENS = [
  {
    id: 'trex_skull',
    name: 'T-Rex Serrated Skull',
    dinosaur: 'Tyrannosaurus Rex',
    period: 'Late Cretaceous (66 Million Years Ago)',
    type: 'Carnivore (Apex Predator)',
    icon: '🦖',
    fact: 'T-Rex had bone-crushing jaws with teeth up to 12 inches long, exerting over 12,000 pounds of bite force!',
    gridPos: 2
  },
  {
    id: 'tri_horns',
    name: 'Triceratops 3-Horn Frill',
    dinosaur: 'Triceratops Prorsus',
    period: 'Late Cretaceous',
    type: 'Herbivore (Plant Eater)',
    icon: '🦏',
    fact: 'The heavy bone frill shielded its neck from predators and was used to regulate body temperature.',
    gridPos: 4
  },
  {
    id: 'raptor_claw',
    name: 'Velociraptor Sickle Claw',
    dinosaur: 'Velociraptor',
    period: 'Late Cretaceous',
    type: 'Carnivore (Agile Hunter)',
    icon: '⚡',
    fact: 'Velociraptors were actually covered in bird-like feathers and hunted with high tactical speed!',
    gridPos: 7
  },
  {
    id: 'stego_plate',
    name: 'Stegosaurus Armor Plate',
    dinosaur: 'Stegosaurus',
    period: 'Late Jurassic (150 Million Years Ago)',
    type: 'Herbivore',
    icon: '🛡️',
    fact: 'Stegosaurus had a brain the size of a walnut, but carried 17 giant kite-shaped plates along its back for defense.',
    gridPos: 8
  },
  {
    id: 'brachio_bone',
    name: 'Brachiosaurus Giant Femur',
    dinosaur: 'Brachiosaurus',
    period: 'Late Jurassic',
    type: 'Giant Sauropod Herbivore',
    icon: '🦕',
    fact: 'Brachiosaurus stood over 40 feet tall and weighed as much as 12 adult African elephants!',
    gridPos: 10
  },
  {
    id: 'amber_fossil',
    name: 'Prehistoric Tree Amber',
    dinosaur: 'Cretaceous Insect In Resin',
    period: 'Early Cretaceous (100M Years)',
    type: 'Biomolecular Preservation',
    icon: '🪰',
    fact: 'Sticky tree sap fossilized into golden amber, preserving microscopic details of prehistoric bugs perfectly for millions of years!',
    gridPos: 5
  }
];

export default function DinoFossilFlow() {
  const { exitRoute, exitTitle } = useGameExitRoute();
  const [isDayMode, setIsDayMode] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTool, setActiveTool] = useState('chisel'); // 'chisel' | 'brush' | 'scanner'

  // Dig Grid state (12 blocks: 0 to 11)
  const initialGrid = Array(12).fill(null).map((_, idx) => {
    const specimen = FOSSIL_SPECIMENS.find(f => f.gridPos === idx);
    return {
      id: idx,
      isDug: false,
      hitsRemaining: specimen ? 2 : 1,
      specimen: specimen || null
    };
  });

  const [gridTiles, setGridTiles] = useState(initialGrid);
  const [excavatedCount, setExcavatedCount] = useState(0);
  const [selectedSpecimen, setSelectedSpecimen] = useState(FOSSIL_SPECIMENS[0]);
  const [isCompleted, setIsCompleted] = useState(false);

  // Student Pass Form
  const [studentName, setStudentName] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [gradeClass, setGradeClass] = useState('Grade 4');
  const [hasClaimedPass, setHasClaimedPass] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  const handleTileDig = (index) => {
    const tile = gridTiles[index];
    if (tile.isDug) {
      if (tile.specimen) setSelectedSpecimen(tile.specimen);
      return;
    }

    playExcavationChiselSound();

    const updated = [...gridTiles];
    if (tile.hitsRemaining > 1) {
      updated[index] = { ...tile, hitsRemaining: tile.hitsRemaining - 1 };
      setGridTiles(updated);
    } else {
      updated[index] = { ...tile, isDug: true, hitsRemaining: 0 };
      setGridTiles(updated);

      if (tile.specimen) {
        setSelectedSpecimen(tile.specimen);
        playCorrectChime();
        const newCount = excavatedCount + 1;
        setExcavatedCount(newCount);

        if (newCount >= FOSSIL_SPECIMENS.length) {
          setTimeout(() => {
            playWinFanfare();
            setIsCompleted(true);
            const code = 'DINO-' + Math.random().toString(36).substring(2, 7).toUpperCase();
            setVoucherCode(code);
          }, 800);
        }
      }
    }
  };

  const handleResetSite = () => {
    setGridTiles(initialGrid);
    setExcavatedCount(0);
    setIsCompleted(false);
    setHasClaimedPass(false);
    setSelectedSpecimen(FOSSIL_SPECIMENS[0]);
  };

  const handleClaimPass = (e) => {
    e.preventDefault();
    if (!studentName.trim()) return;

    setHasClaimedPass(true);
    playLaunchChime();

    // Broadcast winner record
    try {
      const existing = JSON.parse(localStorage.getItem('gamifycenter_school_records') || '[]');
      const newRecord = {
        id: 'STEM-DINO-' + Date.now(),
        studentName: studentName.trim(),
        schoolName: schoolName.trim() || 'Prehistoric Lab Visitor',
        gameTitle: 'Dino Fossil Dig & Paleontologist Lab',
        category: 'Paleontology & Earth Science',
        score: `Excavated All 6 Prehistoric Fossils`,
        prizeWon: 'Junior Chief Paleontologist Badge & Museum Pass',
        voucherCode: voucherCode || ('DINO-' + Math.floor(1000 + Math.random() * 9000)),
        timestamp: new Date().toISOString()
      };
      existing.unshift(newRecord);
      localStorage.setItem('gamifycenter_school_records', JSON.stringify(existing.slice(0, 100)));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDayMode ? 'bg-[#faf6ee] text-stone-900' : 'bg-[#221812] text-stone-100'
    } flex flex-col font-sans select-none overflow-x-hidden`}>

      {/* Header Bar */}
      <header className={`px-4 sm:px-6 py-3 border-b flex flex-wrap items-center justify-between gap-3 sticky top-0 z-30 backdrop-blur-md ${
        isDayMode ? 'bg-[#fffdf9]/95 border-amber-200/90 shadow-sm' : 'bg-[#2a1d16]/95 border-amber-900/50 shadow-lg'
      }`}>
        <div className="flex items-center gap-3">
          <Link 
            to={exitRoute} 
            title={exitTitle}
            className={`p-2 rounded-xl border flex items-center gap-1.5 text-xs font-bold transition-all ${
              isDayMode ? 'bg-white border-amber-300 text-stone-800 hover:bg-amber-50 shadow-xs' : 'bg-stone-900 border-stone-700 text-stone-200 hover:bg-stone-800'
            }`}
          >
            <SafeIcon icon={FiHome} className="text-sm" />
            <span className="hidden sm:inline">Portal</span>
          </Link>

          <div>
            <div className="flex items-center gap-2">
              <span className="text-xl">🦴</span>
              <h1 className="text-base sm:text-lg font-black tracking-wide bg-gradient-to-r from-amber-800 via-orange-600 to-amber-900 dark:from-amber-400 dark:via-yellow-300 dark:to-amber-500 bg-clip-text text-transparent">
                Dino Fossil Dig & Paleontology Lab
              </h1>
              <span className="hidden md:inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/30">
                Earth Science STEM
              </span>
            </div>
            <p className={`text-[11px] hidden sm:block ${isDayMode ? 'text-stone-600 font-medium' : 'text-stone-400'}`}>
              Interactive Prehistoric Dig Site, Fossil Excavation & Jurassic Anatomy
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          {/* Tool Selector */}
          <div className={`p-1 rounded-xl border flex items-center text-xs font-bold ${
            isDayMode ? 'bg-amber-100/80 border-amber-300' : 'bg-stone-900 border-stone-800'
          }`}>
            <button
              onClick={() => setActiveTool('chisel')}
              className={`px-3 py-1 rounded-lg transition-all flex items-center gap-1 ${
                activeTool === 'chisel' 
                  ? 'bg-amber-600 text-white font-black shadow-sm' 
                  : isDayMode ? 'text-stone-700 hover:text-stone-950 font-bold' : 'text-stone-400 hover:text-white'
              }`}
            >
              ⛏️ Chisel
            </button>
            <button
              onClick={() => setActiveTool('brush')}
              className={`px-3 py-1 rounded-lg transition-all flex items-center gap-1 ${
                activeTool === 'brush' 
                  ? 'bg-amber-600 text-white font-black shadow-sm' 
                  : isDayMode ? 'text-stone-700 hover:text-stone-950 font-bold' : 'text-stone-400 hover:text-white'
              }`}
            >
              🖌️ Whisk Brush
            </button>
          </div>

          {/* Reset Site */}
          <button
            onClick={handleResetSite}
            className={`p-2 rounded-xl border text-xs font-bold transition-all ${
              isDayMode ? 'bg-white border-amber-300 text-stone-800 hover:bg-amber-50 shadow-xs' : 'bg-stone-900 border-stone-800 text-stone-300 hover:bg-stone-800'
            }`}
            title="Reset Dig Site"
          >
            <SafeIcon icon={FiRefreshCw} className="text-sm" />
          </button>

          {/* Day / Night */}
          <button
            onClick={() => setIsDayMode(!isDayMode)}
            className={`p-2 rounded-xl border transition-all ${
              isDayMode ? 'bg-white border-amber-300 text-amber-700 hover:bg-amber-50 shadow-xs' : 'bg-stone-900 border-stone-800 text-amber-400'
            }`}
            title="Toggle Day/Night"
          >
            <SafeIcon icon={isDayMode ? FiMoon : FiSun} className="text-sm" />
          </button>

          {/* Fullscreen */}
          <button
            onClick={toggleFullscreen}
            className={`p-2 rounded-xl border transition-all ${
              isDayMode ? 'bg-white border-amber-300 text-stone-800 hover:bg-amber-50 shadow-xs' : 'bg-stone-900 border-stone-800 text-stone-300 hover:text-white'
            }`}
            title="Stage Fullscreen"
          >
            <SafeIcon icon={isFullscreen ? FiMinimize2 : FiMaximize2} className="text-sm" />
          </button>
        </div>
      </header>

      {/* Main Digging Arena */}
      <main className="flex-1 p-3 sm:p-6 max-w-7xl w-full mx-auto flex flex-col gap-6">

        {/* Progress & Paleontology Stats */}
        <div className={`p-4 rounded-3xl border flex flex-wrap items-center justify-between gap-4 ${
          isDayMode ? 'bg-white border-amber-200/90 shadow-sm' : 'bg-[#2b1f18] border-amber-900/50'
        }`}>
          <div>
            <span className={`text-[10px] font-black uppercase tracking-wider block ${
              isDayMode ? 'text-amber-700' : 'text-amber-400'
            }`}>
              Excavation Progress
            </span>
            <h2 className={`text-lg font-black flex items-center gap-2 ${
              isDayMode ? 'text-stone-900' : 'text-stone-100'
            }`}>
              <span>Fossils Unearthed: {excavatedCount} / {FOSSIL_SPECIMENS.length}</span>
              {excavatedCount === FOSSIL_SPECIMENS.length && (
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/40 font-bold">
                  ✓ Dig Complete
                </span>
              )}
            </h2>
          </div>

          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold ${isDayMode ? 'text-stone-600' : 'text-stone-400'}`}>Current Tool:</span>
            <span className={`px-3 py-1 rounded-xl text-xs font-black border ${
              isDayMode 
                ? 'bg-amber-100 border-amber-300 text-amber-900 shadow-xs' 
                : 'bg-amber-600/20 border-amber-600/40 text-amber-400'
            }`}>
              {activeTool === 'chisel' ? '⛏️ Stone Chisel (Breaks Hard Rock)' : '🖌️ Sediment Brush (Sweeps Dust)'}
            </span>
          </div>
        </div>

        {/* Grid & Specimen Anatomy View */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          
          {/* Left: 4x3 Interactive Dig Site Quarry (7 cols) */}
          <div className={`lg:col-span-7 p-6 rounded-3xl border-2 flex flex-col gap-4 ${
            isDayMode 
              ? 'bg-[#f4ebe0] border-amber-300 shadow-md' 
              : 'bg-[#251a13] border-stone-800 shadow-2xl'
          }`}>
            <div className="flex justify-between items-center">
              <div>
                <h3 className={`text-sm font-black uppercase tracking-wider ${
                  isDayMode ? 'text-amber-900' : 'text-amber-400'
                }`}>
                  Prehistoric Excavation Grid
                </h3>
                <p className={`text-xs ${isDayMode ? 'text-stone-600 font-medium' : 'text-stone-400'}`}>
                  Tap or click sedimentary blocks to break rock & sweep dust
                </p>
              </div>
              <span className={`text-xs font-black px-2.5 py-1 rounded-xl border ${
                isDayMode 
                  ? 'bg-amber-200 text-amber-900 border-amber-400 shadow-xs' 
                  : 'text-amber-400 bg-amber-600/20 border-amber-600/30'
              }`}>
                Active Quarry
              </span>
            </div>

            {/* 4x3 Matrix */}
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 sm:gap-4 my-2">
              {gridTiles.map((tile) => {
                const isDug = tile.isDug;
                const hasSpecimen = Boolean(tile.specimen);
                const isCrack1 = tile.hitsRemaining === 1 && !isDug && hasSpecimen;

                return (
                  <motion.button
                    key={tile.id}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.96 }}
                    onClick={() => handleTileDig(tile.id)}
                    className={`aspect-square rounded-2xl border-2 relative overflow-hidden flex flex-col items-center justify-center transition-all shadow-md ${
                      isDug
                        ? hasSpecimen
                          ? isDayMode 
                            ? 'bg-gradient-to-br from-amber-50 to-orange-100 border-amber-500 shadow-lg shadow-amber-500/20' 
                            : 'bg-gradient-to-br from-amber-700/40 to-stone-900 border-amber-500 shadow-amber-500/20'
                          : isDayMode 
                            ? 'bg-[#ece3d1] border-stone-300 text-stone-600 font-bold' 
                            : 'bg-stone-900/60 border-stone-800 opacity-60'
                        : isCrack1
                          ? isDayMode 
                            ? 'bg-gradient-to-br from-[#dfb97a] to-[#c5954a] border-amber-700 text-amber-950 shadow-md cursor-pointer' 
                            : 'bg-amber-900/40 border-amber-600/80 cursor-pointer'
                          : isDayMode
                            ? 'bg-gradient-to-br from-[#ebe0ca] to-[#d8c29b] border-[#bfa275] text-amber-950 hover:from-[#f3e9d6] hover:to-[#e2cca7] hover:border-amber-700 cursor-pointer'
                            : 'bg-[#38261a] border-[#4f3624] hover:border-amber-600/70 cursor-pointer'
                    }`}
                  >
                    {/* Buried State */}
                    {!isDug ? (
                      <div className="flex flex-col items-center justify-center p-2 text-center">
                        <span className="text-2xl sm:text-3xl opacity-90 drop-shadow-sm">
                          {isCrack1 ? '⚡' : '🪨'}
                        </span>
                        <span className={`text-[10px] font-black uppercase mt-1 ${
                          isDayMode ? 'text-amber-950 font-black' : 'text-stone-300'
                        }`}>
                          {isCrack1 ? 'Cracked' : 'Sediment'}
                        </span>
                      </div>
                    ) : (
                      /* Unearthed State */
                      <div className="flex flex-col items-center justify-center p-2 text-center animate-fadeIn">
                        {hasSpecimen ? (
                          <>
                            <span className="text-3xl sm:text-4xl filter drop-shadow-md">
                              {tile.specimen.icon}
                            </span>
                            <span className={`text-[10px] font-black mt-1 line-clamp-1 ${
                              isDayMode ? 'text-amber-900 font-black' : 'text-amber-300'
                            }`}>
                              {tile.specimen.name.split(' ')[0]}
                            </span>
                          </>
                        ) : (
                          <span className={`text-xs font-bold ${isDayMode ? 'text-stone-600' : 'text-stone-500'}`}>
                            Barren Sand
                          </span>
                        )}
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>

            <div className={`p-3 rounded-2xl border text-xs flex items-center justify-between ${
              isDayMode 
                ? 'bg-amber-100/70 border-amber-300 text-stone-800' 
                : 'bg-black/30 border-stone-800 text-stone-300'
            }`}>
              <span>💡 <strong>Field Tip:</strong> Some dense fossil stone requires 2 strikes to fully uncover!</span>
              <span className={isDayMode ? 'text-amber-900 font-black' : 'text-amber-400 font-bold'}>
                Depth: 15m Cretaceous Layer
              </span>
            </div>
          </div>

          {/* Right: Paleontologist Field Notes & Specimen Details (5 cols) */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            
            {/* Active Specimen Inspector */}
            <div className={`p-6 rounded-3xl border-2 flex-1 flex flex-col justify-between gap-4 ${
              isDayMode ? 'bg-white border-amber-200 shadow-md text-stone-900' : 'bg-[#2b1f18] border-stone-800 shadow-xl'
            }`}>
              <div>
                <div className={`flex items-center justify-between border-b pb-3 mb-3 ${
                  isDayMode ? 'border-amber-200' : 'border-stone-800'
                }`}>
                  <div>
                    <span className={`text-[10px] uppercase font-black tracking-wider block ${
                      isDayMode ? 'text-amber-800' : 'text-amber-400'
                    }`}>
                      Field Specimen Telemetry
                    </span>
                    <h3 className={`text-lg font-black flex items-center gap-2 ${
                      isDayMode ? 'text-stone-900' : 'text-stone-100'
                    }`}>
                      <span>{selectedSpecimen.icon}</span>
                      <span>{selectedSpecimen.name}</span>
                    </h3>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase border ${
                    isDayMode 
                      ? 'bg-amber-100 text-amber-900 border-amber-300 shadow-xs' 
                      : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                  }`}>
                    {selectedSpecimen.type}
                  </span>
                </div>

                <div className="space-y-3 text-xs">
                  <div className={`p-3 rounded-2xl border ${
                    isDayMode ? 'bg-amber-50/80 border-amber-200 text-stone-900' : 'bg-stone-900 border-stone-800'
                  }`}>
                    <span className={`text-[10px] font-bold uppercase block ${
                      isDayMode ? 'text-amber-800' : 'text-stone-400'
                    }`}>Dinosaur Taxon</span>
                    <strong className={`text-sm ${
                      isDayMode ? 'text-amber-950 font-black' : 'text-amber-400 font-bold'
                    }`}>{selectedSpecimen.dinosaur}</strong>
                  </div>

                  <div className={`p-3 rounded-2xl border ${
                    isDayMode ? 'bg-sky-50/80 border-sky-200 text-stone-900' : 'bg-stone-900 border-stone-800'
                  }`}>
                    <span className={`text-[10px] font-bold uppercase block ${
                      isDayMode ? 'text-sky-800' : 'text-stone-400'
                    }`}>Geological Era & Age</span>
                    <strong className={isDayMode ? 'text-sky-950 font-black' : 'text-sky-400'}>
                      {selectedSpecimen.period}
                    </strong>
                  </div>

                  <div className={`p-3.5 rounded-2xl border ${
                    isDayMode ? 'bg-amber-100/70 border-amber-300 text-stone-900' : 'bg-amber-950/30 border-amber-800/50 text-stone-200'
                  }`}>
                    <span className={`text-[10px] font-black uppercase tracking-wider block mb-1 ${
                      isDayMode ? 'text-amber-900' : 'text-amber-400'
                    }`}>
                      Anatomical & STEM Fact
                    </span>
                    <p className="leading-relaxed font-medium">{selectedSpecimen.fact}</p>
                  </div>
                </div>
              </div>

              {/* All Fossils Collection Strip */}
              <div>
                <span className={`text-[10px] font-bold uppercase block mb-2 ${
                  isDayMode ? 'text-stone-700' : 'text-stone-400'
                }`}>
                  Museum Expedition Collection:
                </span>
                <div className="grid grid-cols-6 gap-2">
                  {FOSSIL_SPECIMENS.map((f) => {
                    const isDiscovered = gridTiles.some(t => t.specimen?.id === f.id && t.isDug);
                    return (
                      <button
                        key={f.id}
                        onClick={() => setSelectedSpecimen(f)}
                        className={`aspect-square rounded-xl border flex flex-col items-center justify-center transition-all ${
                          isDiscovered
                            ? isDayMode 
                              ? 'bg-amber-100 border-2 border-amber-500 text-amber-950 shadow-xs' 
                              : 'bg-amber-600/20 border-amber-500 text-amber-400'
                            : isDayMode 
                              ? 'bg-stone-100 border-stone-300 opacity-60 text-stone-500' 
                              : 'bg-stone-900 border-stone-800 opacity-40 text-stone-600'
                        }`}
                        title={f.name}
                      >
                        <span className="text-lg">{f.icon}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Win & Claim Certificate Section */}
            {isCompleted && (
              <div className={`p-6 rounded-3xl border-2 text-center ${
                isDayMode 
                  ? 'border-amber-400 bg-gradient-to-br from-amber-50 via-white to-orange-50 shadow-xl text-stone-900' 
                  : 'border-amber-500 bg-gradient-to-br from-amber-950/60 to-stone-900 shadow-2xl text-stone-100'
              }`}>
                <ConfettiEffect />
                <span className="text-4xl block mb-2">🏆</span>
                <h3 className={`text-lg font-black ${isDayMode ? 'text-amber-900' : 'text-amber-400'}`}>
                  All Prehistoric Fossils Uncovered!
                </h3>
                <p className={`text-xs mt-1 mb-4 ${isDayMode ? 'text-stone-700' : 'text-stone-300'}`}>
                  Outstanding fieldwork! Claim your <strong>Junior Chief Paleontologist Certificate & Museum Pass</strong>.
                </p>

                {!hasClaimedPass ? (
                  <form onSubmit={handleClaimPass} className="flex flex-col gap-3 text-left">
                    <div>
                      <label className={`text-xs font-bold block mb-1 ${isDayMode ? 'text-stone-700' : 'text-stone-400'}`}>
                        Explorer's Full Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={studentName}
                        onChange={(e) => setStudentName(e.target.value)}
                        placeholder="e.g. Diya Patel"
                        className={`w-full p-2.5 rounded-xl border text-xs font-bold focus:outline-none ${
                          isDayMode 
                            ? 'bg-white border-amber-300 text-stone-900 focus:border-amber-600' 
                            : 'bg-stone-900 border-stone-700 text-white focus:border-amber-400'
                        }`}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className={`text-xs font-bold block mb-1 ${isDayMode ? 'text-stone-700' : 'text-stone-400'}`}>
                          School / Camp
                        </label>
                        <input
                          type="text"
                          value={schoolName}
                          onChange={(e) => setSchoolName(e.target.value)}
                          placeholder="e.g. National Public School"
                          className={`w-full p-2.5 rounded-xl border text-xs font-bold focus:outline-none ${
                            isDayMode 
                              ? 'bg-white border-amber-300 text-stone-900 focus:border-amber-600' 
                              : 'bg-stone-900 border-stone-700 text-white focus:border-amber-400'
                          }`}
                        />
                      </div>
                      <div>
                        <label className={`text-xs font-bold block mb-1 ${isDayMode ? 'text-stone-700' : 'text-stone-400'}`}>
                          Grade
                        </label>
                        <select
                          value={gradeClass}
                          onChange={(e) => setGradeClass(e.target.value)}
                          className={`w-full p-2.5 rounded-xl border text-xs font-bold focus:outline-none ${
                            isDayMode 
                              ? 'bg-white border-amber-300 text-stone-900 focus:border-amber-600' 
                              : 'bg-stone-900 border-stone-700 text-white focus:border-amber-400'
                          }`}
                        >
                          <option>Pre-K / UKG</option>
                          <option>Grade 1</option>
                          <option>Grade 2</option>
                          <option>Grade 3</option>
                          <option>Grade 4</option>
                          <option>Grade 5</option>
                          <option>Grade 6+</option>
                        </select>
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="mt-2 w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-stone-950 font-black text-xs uppercase tracking-wider hover:brightness-110 shadow-lg cursor-pointer"
                    >
                      Issue Official Paleontology Pass 📜
                    </button>
                  </form>
                ) : (
                  <div className="space-y-3 text-left">
                    <div className={`p-4 rounded-2xl border text-xs ${
                      isDayMode 
                        ? 'bg-white/95 border-amber-300 shadow-sm text-stone-800' 
                        : 'bg-black/50 border-amber-500/40 text-stone-300'
                    }`}>
                      <div className="flex justify-between items-center border-b border-amber-200 dark:border-stone-800 pb-2 mb-2">
                        <span className={`font-bold ${isDayMode ? 'text-amber-800' : 'text-amber-400'}`}>
                          PALEONTOLOGY ACCREDITATION
                        </span>
                        <span className={`font-mono font-black ${isDayMode ? 'text-amber-700' : 'text-amber-300'}`}>
                          {voucherCode}
                        </span>
                      </div>
                      <p className={isDayMode ? 'text-stone-700' : 'text-stone-300'}>
                        Name: <strong className={isDayMode ? 'text-stone-950 font-black' : 'text-white'}>{studentName}</strong>
                      </p>
                      <p className={isDayMode ? 'text-stone-600' : 'text-stone-400'}>
                        School: <strong>{schoolName || 'Stall Visitor'} ({gradeClass})</strong>
                      </p>
                      <p className="text-emerald-600 dark:text-emerald-400 font-bold mt-1">
                        Status: Certified Master Fossil Excavator
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => window.print()}
                        className="flex-1 py-2.5 rounded-xl bg-amber-500 text-stone-950 font-black text-xs uppercase hover:bg-amber-400 cursor-pointer shadow-sm"
                      >
                        🖨️ Print Pass
                      </button>
                      <button
                        onClick={handleResetSite}
                        className={`px-4 py-2.5 rounded-xl border text-xs font-bold cursor-pointer ${
                          isDayMode 
                            ? 'bg-white border-amber-300 text-stone-800 hover:bg-amber-50' 
                            : 'bg-stone-800 border-stone-700 text-stone-300 hover:bg-stone-700'
                        }`}
                      >
                        Dig Again ➜
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
